import React from "react";
import PropTypes from "prop-types";
import { Label } from "../../Components/Label";
import { OptionIndex } from "../../Components/OptionIndex";
import { OptionFeedback } from "../../Components/OptionFeedback";
import { OptionWrapper } from "../../Components/OptionWrapper";
import { OverlaySlider } from "../../Components/OverlaySlider";
import { SliderGroup } from "../../Components/SliderGroup";
import { Utilities } from "@learningblocks/exp-constants";
import { NotebookImage } from "@learningblocks/simulations/shared-components";
import constants from "../../Components/OptionIndex/OptionIndex.constants";

const { COMPONENT_TYPE } = Utilities;

/**
 * Option:
 *  - Option Component for SingleSelect Component
 */
export function Option(props) {
  const { isPredictionQuestion = false } = props;
  const selectOption = () => {
    props.disabled === false &&
      props.selectOption({
        optionID: props.id,
        checked: !props.selected,
      });
  };

  const getOptionClassName = () => {
    if (props.selected && props.disabled) {
      if (props.isCorrect) return "cscq__option correct";
      if (props.selected) return "cscq__option incorrect";
      return "cscq__option";
    } else if (props.disabled) {
      return "cscq__option disabled";
    } else if (props.selected) return "cscq__option selected";
    else return "cscq__option";
  };

  const getFeedbackClassName = () => {
    if (props.isCorrect) return "cscq__feedback_correct";
    else return "cscq__feedback_incorrect";
  };

  const getLabelClassName = () => {
    if (props.selected && props.disabled) {
      if (props.isCorrect) return "cscq__label correct";
      else return "cscq__label incorrect";
    }
  };

  const renderOverlaySlider = () => (
    <OverlaySlider
      sequence={props.sequence}
      highlight={[props.current.start, props.current.end - props.current.start]}
      highlightClass={props.highlightClass}
      disable={true}
    />
  );

  const renderSequenceSliderGroup = () => (
    <SliderGroup id={props.id} sliders={props.sliders} />
  );

  const renderNotebookImage = () => (
    <NotebookImage {...{ image: props.image }} key={props.id} />
  );

  const renderLabel = () => (
    <Label label={props.text} id={props.id} className={getLabelClassName()} />
  );

  const renderOptionContent = ({ type }) => {
    switch (type) {
      case COMPONENT_TYPE.SEQUENCE_SLIDER:
        return renderOverlaySlider();
      case COMPONENT_TYPE.SEQUENCE_SLIDER_GROUP:
        return renderSequenceSliderGroup();
      case COMPONENT_TYPE.NOTEBOOK_IMAGE:
        return renderNotebookImage();
      case COMPONENT_TYPE.LABEL:
      default:
        return renderLabel();
    }
  };

  const getAriaLabel = () => {
    const optionLetter = constants.ALPHABET_LIST[props.index];
    let contentLabel = "";

    if (props.type === COMPONENT_TYPE.NOTEBOOK_IMAGE && props.image?.altText) {
      contentLabel = props.image.altText;
    } else if (props.text && typeof props.text === "string") {
      contentLabel = props.text;
    }

    return contentLabel ? `${optionLetter}. ${contentLabel}` : optionLetter;
  };

  return (
    <>
      <OptionWrapper
        className={getOptionClassName()}
        selectOption={selectOption}
        ariaLabel={getAriaLabel()}
      >
        <OptionIndex
          id={props.id}
          index={props.index}
          isPredictionQuestion={isPredictionQuestion}
          selected={props.selected}
          disabled={props.disabled}
          isCorrect={props.isCorrect}
        />
        {renderOptionContent(props)}
      </OptionWrapper>
      <OptionFeedback
        visible={props.disabled}
        className={getFeedbackClassName()}
        isCorrect={props.isCorrect}
        text={
          props.showFeedback || props.showFeedbackByDefault || props.selected
            ? props.feedback
            : ""
        }
      />
    </>
  );
}

Option.propTypes = {
  /**
   * Custom className to be set on component
   */
  className: PropTypes.string,
  /**
   * The feedback to be displayed for the particular option
   */
  feedback: PropTypes.string,
  /**
   * ID for the particular prop
   */
  id: PropTypes.number.isRequired,
  /**
   * Index of the option labels
   */
  index: PropTypes.number.isRequired,
  /**
   * Boolean value specifying if the option is correct
   */
  isCorrect: PropTypes.bool.isRequired,
  /**
   * Boolean value specifying if the option is selected
   */
  selected: PropTypes.bool,
  /**
   * Boolean value specifying if the option is disabled
   */
  disabled: PropTypes.bool.isRequired,
  /**
   * Boolean value specifying if the option is disabled
   */
  isPredictionQuestion: PropTypes.bool,
  /**
   * Callback function to be involked when selecting the option
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * The currently highlighted content
   */
  current: PropTypes.shape({
    /**
     * Text to be displayed in slider select
     */
    text: PropTypes.string,
    /**
     * The starting point of the highlighted text
     */
    start: PropTypes.string,
  }),
  /**
   * The string sequence to be displayed.
   */
  sequence: PropTypes.string,
  /**
   * The array to set the color of highlighting
   */
  highlightClass: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
  /**
   * Text for label
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  /**
   * for keyboard navigation
   */
  tabIndex: PropTypes.string,
  /**
   * show feedback
   */
  showFeedback: PropTypes.bool,
  /**
   * show feedback
   */
  showFeedbackByDefault: PropTypes.bool,
  /**
   * Type of option content (e.g., NOTEBOOK_IMAGE, LABEL)
   */
  type: PropTypes.string,
  /**
   * Image object for NOTEBOOK_IMAGE type options
   */
  image: PropTypes.shape({
    src: PropTypes.string,
    altText: PropTypes.string,
    caption: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
};
